/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuid } from "uuid";

export type TaskStatus = 'pending' | 'uploading' | 'success' | 'error';

export type UploadMode = 'sequential' | 'parallel';

export interface UploadOptions<T = unknown> {
  /** 上传模式，默认 sequential（顺序上传），可传 parallel（并行上传） */
  mode?: UploadMode;
  /** parallel 模式下的最大并发数，默认 Infinity */
  concurrency?: number;
  /** 每个任务完成后的回调 */
  onTaskComplete?: (task: UploadTask<T>, result?: T) => void;
  /** 所有任务完成后的回调 */
  onAllComplete?: (tasks: UploadTask<T>[]) => void;
}

export interface UploadTask<T = unknown> {
  taskId: string;
  name: string;
  size: number;
  file: File;
  status: TaskStatus;
  type: string;
  progress: number;
  // 每个任务携带自己的上传执行函数（signal 在关闭面板时 abort，用于取消进行中的请求）
  uploader: (file: File, onProgress: (percent: number) => void, signal: AbortSignal) => Promise<T>;
}

// 排除 void 类型
type NonVoid<T> = T extends void ? never : T;

interface UploadContextType {
  tasks: UploadTask[];
  isVisible: boolean;
  isCollapsed: boolean;
  setIsVisible: (show: boolean) => void;
  setIsCollapsed: (collapsed: boolean) => void;
  handleClose: () => void;
  addUploadTasks<T>(
    files: File[],
    uploader: (
      file: File,
      onProgress: (percent: number) => void,
      signal: AbortSignal
    ) => Promise<NonVoid<T>>,
    options?: UploadOptions<T>
  ): void;
}

export const UploadContext = createContext<UploadContextType | null>(null);

export const UploadProvider = ({ children }: { children: React.ReactNode }) => {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // 上传模式（sequential 或 parallel）
  const uploadModeRef = useRef<UploadMode>('sequential');
  // parallel 模式下的最大并发数，默认 Infinity
  const concurrencyRef = useRef<number>(Infinity);
  // 单任务完成回调
  const onTaskCompleteRef = useRef<UploadOptions['onTaskComplete']>();
  // 全部任务完成回调
  const onAllCompleteRef = useRef<UploadOptions['onAllComplete']>();

  // 用于标记当前是否正在处理队列，避免并发冲突
  const isProcessingRef = useRef(false);

  // 用于存储自动隐藏的定时器
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoHideDelay = 8000; // 8秒自动隐藏

  // 当前上传会话的 AbortController（关闭/清空时 abort，取消仍在进行的 HTTP）
  const uploadSessionAbortRef = useRef<AbortController | null>(null);
  // 关闭或清空队列后忽略异步完成回调，避免在任务已清空后仍触发 onTaskComplete
  const uploadCancelledRef = useRef(false);

  // 添加任务
  const addUploadTasks = useCallback((
    files: File[],
    uploader: (file: File, onProgress: (percent: number) => void, signal: AbortSignal) => Promise<any>,
    options?: UploadOptions
  ) => {
    uploadCancelledRef.current = false;
    if (!uploadSessionAbortRef.current || uploadSessionAbortRef.current?.signal.aborted) {
      uploadSessionAbortRef.current = new AbortController();
    }

    // 清空之前的自动隐藏定时器
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    // 仅当显式传入 options 时才更新策略
    if (options?.mode) {
      uploadModeRef.current = options.mode;
    }
    if (options?.concurrency !== undefined) {
      concurrencyRef.current = options.concurrency;
    }
    if (options?.onTaskComplete) {
      onTaskCompleteRef.current = options.onTaskComplete;
    }
    if (options?.onAllComplete) {
      onAllCompleteRef.current = options.onAllComplete;
    }

    const newTasks: UploadTask[] = files.map(file => ({
      ...file,
      taskId: uuid(),
      name: file.name,
      size: file.size,
      file: file,
      status: 'pending',
      type: file.type,
      progress: 0,
      uploader, // 绑定执行器
    }));

    // 任务追加到队列
    setTasks(prev => [...prev, ...newTasks]);

    setIsVisible(true); // 开始任务时自动展示
    setIsCollapsed(false);
  }, []);

  // 更新任务状态
  const updateTask = (taskId: string, payload: Partial<UploadTask>) => {
    setTasks(prev => prev.map(t => t.taskId === taskId ? { ...t, ...payload } : t));
  };

  // 清空任务队列
  const clearTasks = useCallback(() => {
    uploadCancelledRef.current = true;
    uploadSessionAbortRef.current?.abort();
    uploadSessionAbortRef.current = null;

    setTasks([]);
    setIsVisible(false);
    setIsCollapsed(true);

    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, []);

  // 检查所有任务是否已完成
  const allTasksCompleted = useCallback(() => {
    return tasks.length > 0 && tasks.every(task =>
      task.status === 'success' || task.status === 'error'
    );
  }, [tasks]);

  // 关闭
  const handleClose = useCallback(() => {
    clearTasks();
  }, [clearTasks]);

  // 顺序执行任务
  const runSequential = async () => {
    if (isProcessingRef.current) return;

    // 找到第一个等待中的任务
    const nextTask = tasks.find(t => t.status === 'pending');

    // 如果没有任务或之前的任务还在上传中（这里假设是严格单线程顺序，如果支持并发需调整逻辑）
    const isAnyUploading = tasks.some(t => t.status === 'uploading');

    if (!nextTask || isAnyUploading) return;

    isProcessingRef.current = true;

    const signal = uploadSessionAbortRef.current?.signal;
    if (!signal || signal.aborted) {
      isProcessingRef.current = false;
      return;
    }

    try {
      // 1. 标记为上传中
      updateTask(nextTask.taskId, { status: 'uploading', progress: 0 });

      // 2. 执行外部传入的上传函数
      const result = await nextTask.uploader(nextTask.file, percent => {
        updateTask(nextTask.taskId, { progress: percent });
      }, signal);

      // 3. 标记完成
      if (uploadCancelledRef.current) return;
      updateTask(nextTask.taskId, { status: 'success', progress: 100 });
      onTaskCompleteRef.current?.(nextTask, result);
    } catch (err) {
      if (uploadCancelledRef.current) return;
      console.error('Upload failed', err);
      updateTask(nextTask.taskId, { status: 'error' });
      onTaskCompleteRef.current?.(nextTask);
    } finally {
      isProcessingRef.current = false;
    }
  };

  // 并行执行 + 并发上限调度
  const runParallelWithLimit = () => {
    const concurrency = concurrencyRef.current;

    const uploadingCount = tasks.filter(t => t.status === 'uploading').length;
    const availableSlots = concurrency - uploadingCount;
    if (availableSlots <= 0) return;

    // 从待处理任务中取出最多 availableSlots 个任务
    const pendingTasks = tasks
      .filter(t => t.status === 'pending')
      .slice(0, availableSlots);

    if (!pendingTasks.length) return;

    const signal = uploadSessionAbortRef.current?.signal;
    if (!signal || signal.aborted) return;

    pendingTasks.forEach(task => {
      updateTask(task.taskId, { status: 'uploading', progress: 0 });

      task.uploader(task.file, percent => {
        updateTask(task.taskId, { progress: percent });
      }, signal)
        .then(result => {
          if (uploadCancelledRef.current) return;
          updateTask(task.taskId, { status: 'success', progress: 100 });
          onTaskCompleteRef.current?.(task, result);
        })
        .catch(err => {
          if (uploadCancelledRef.current) return;
          console.error('Upload failed', err);
          updateTask(task.taskId, { status: 'error' });
          onTaskCompleteRef.current?.(task);
        });
    });
  };

  // 处理自动隐藏逻辑
  useEffect(() => {
    if (allTasksCompleted()) {
      // 清空之前的定时器（如果有）
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }

      // 设置3秒后自动隐藏
      autoHideTimerRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsCollapsed(true);
        autoHideTimerRef.current = null;
        clearTasks();
      }, autoHideDelay);
    }

    // 如果有新任务开始，清除自动隐藏定时器
    if (tasks.some(task => task.status === 'pending' || task.status === 'uploading')) {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    }

    // 清理函数：组件卸载时清除定时器
    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, [tasks, allTasksCompleted]);

  // 队列处理逻辑 (顺序执行)
  useEffect(() => {
    if (uploadModeRef.current === 'parallel') {
      runParallelWithLimit();
    } else {
      runSequential();
    }
  }, [tasks]);

  // 所有任务完成时触发回调
  useEffect(() => {
    if (allTasksCompleted()) {
      onAllCompleteRef.current?.(tasks);
    }
  }, [tasks, allTasksCompleted]);

  return (
    <UploadContext.Provider value={{
      tasks,
      isVisible,
      isCollapsed,
      setIsVisible,
      setIsCollapsed,
      addUploadTasks,
      handleClose
    }}>
      {children}
    </UploadContext.Provider>
  );
};